const LOG_PREFIX = '[NeoLinkedInChannel]';
/** Bump when changing detection — confirms bundle in DevTools filter. */
export const LINKEDIN_CHANNEL_DEBUG_BUILD = '2026-05-20-v15';

const sequenceTouchPlanCache = new Map();
const diyPlaybookStepsCache = new Map();
const diyPlaybookCatalogCache = new Map();
const diyPlaybookFetchFailedKeys = new Set();

const DIY_PLAYBOOK_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const STATIC_LEARNWISE_PLAYBOOK_KEY_RE = /^learnwise_[a-z0-9_]+_v\d+$/i;

/** Filter DevTools console by `NeoLinkedInChannel`. Set `localStorage.neo_debug_linkedin_channel='0'` to silence. */
export function isLinkedInChannelDebugEnabled() {
  try {
    return localStorage.getItem('neo_debug_linkedin_channel') !== '0';
  } catch {
    return true;
  }
}

function logLinkedInChannel(step, detail) {
  if (!isLinkedInChannelDebugEnabled()) return;
  console.log(LOG_PREFIX, LINKEDIN_CHANNEL_DEBUG_BUILD, step, detail);
}

if (typeof window !== 'undefined' && isLinkedInChannelDebugEnabled()) {
  console.log(
    LOG_PREFIX,
    LINKEDIN_CHANNEL_DEBUG_BUILD,
    'debug on — set localStorage.neo_debug_linkedin_channel="0" to silence',
  );
}

/** Every channel-related field we inspect (order = priority for primary channel). */
const PRIMARY_CHANNEL_KEYS = [
  'channel',
  'touch_channel',
  'touchChannel',
  'step_channel',
  'stepChannel',
  'last_sent_channel',
  'lastSentChannel',
  'message_channel',
  'messageChannel',
  'outreach_channel',
  'outreachChannel',
];

const SECONDARY_CHANNEL_KEYS = [
  'linkedin_sub_type',
  'linkedinSubType',
  'linkedin_message_type',
  'linkedinMessageType',
  'message_type',
  'messageType',
  'channel_type',
  'channelType',
  'touch_type',
  'touchType',
  'touch_kind',
  'touchKind',
  'is_connection_request',
  'isConnectionRequest',
];

function readNestedChannel(row, path) {
  const parts = path.split('.');
  let cur = row;
  for (const p of parts) {
    if (!cur || typeof cur !== 'object') return '';
    cur = cur[p];
  }
  return cur != null && String(cur).trim() !== '' ? String(cur).trim() : '';
}

/** Snapshot of all channel-ish fields on a draft/step/touch row (for debugging). */
export function collectLinkedInChannelDebugSnapshot(source) {
  const row = typeof source === 'object' && source !== null ? source : { channel: source };
  const fields = {};
  for (const key of PRIMARY_CHANNEL_KEYS) {
    if (row[key] != null && String(row[key]).trim() !== '') fields[key] = row[key];
  }
  for (const key of SECONDARY_CHANNEL_KEYS) {
    if (row[key] != null && String(row[key]).trim() !== '') fields[key] = row[key];
  }
  const nested = {
    'step.channel': readNestedChannel(row, 'step.channel'),
    'touch.channel': readNestedChannel(row, 'touch.channel'),
    'sequence.channel': readNestedChannel(row, 'sequence.channel'),
  };
  for (const [k, v] of Object.entries(nested)) {
    if (v) fields[k] = v;
  }
  return fields;
}

export function logLinkedInChannelResolution(context, source) {
  if (!isLinkedInChannelDebugEnabled()) return;
  const fields = collectLinkedInChannelDebugSnapshot(source);
  logLinkedInChannel(context, {
    fields,
    primary: getOutboxChannelRaw(source),
    resolvedChannel: resolveOutboxLinkedInChannel(source),
    diyLinkedInChannel: source?.__diyLinkedInChannel ?? null,
    diyPlaybookLinkedInChannel: source?.__diyPlaybookLinkedInChannel ?? null,
    playbookId: getPlaybookIdForOutboxDraft(source) || null,
    templateChannelRaw: source?.__linkedinTemplateChannelRaw ?? null,
    linkedinTouchOrder: getLinkedInTouchOrder(source),
    priorExecutedLinkedIn: source?.__linkedinPriorExecuted ?? null,
    linkedinTimelineIndex: source?.__linkedinTimelineIndex ?? null,
    templateStage: source?.__linkedinTemplateStage ?? null,
    isLinkedIn: isLinkedInOutboxChannel(source),
    isConnectionRequest: isLinkedInConnectionRequest(source),
    subtypeLabel: getLinkedInOutboxSubtypeLabel(source),
    displayLabel: getChannelDisplayLabel(source),
  });
}

export function getOutboxStageNo(source) {
  const row = typeof source === 'object' && source !== null ? source : {};
  const n = Number(
    row.stage ??
      row.stage_no ??
      row.stageNo ??
      row.currentStage ??
      row.touch_number ??
      row.touchNumber ??
      0,
  );
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** 1-based index of this row among LinkedIn touches in its sequence (set by annotateLinkedInTouchOrderInSequence). */
export function getLinkedInTouchOrder(source) {
  const row = typeof source === 'object' && source !== null ? source : {};
  const annotated = row.__linkedinTouchOrder ?? row.linkedinTouchOrder;
  if (annotated != null) {
    const n = Number(annotated);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  if (Array.isArray(row.allSteps) && row.touch) {
    return resolveLinkedInTouchOrderByTouchNumber(row.allSteps, row.touch);
  }
  if (Array.isArray(row.allSteps) && row.stepIndex != null) {
    return resolveLinkedInTouchOrderInSteps(row.allSteps, row.stepIndex);
  }
  return null;
}

/** Use touch_number / order when the full playbook touch list is available. */
export function resolveLinkedInTouchOrderByTouchNumber(steps, touch) {
  if (!Array.isArray(steps) || !touch) return null;
  const nums = steps
    .filter((s) => isLinkedInOutboxChannel({ channel: s?.channel }))
    .map((s) => Number(s.touch_number ?? s.order ?? 0))
    .filter((n) => n > 0);
  if (!nums.length) return null;
  const sorted = [...new Set(nums)].sort((a, b) => a - b);
  const current = Number(touch.touch_number ?? touch.order ?? 0);
  if (!current) return null;
  const idx = sorted.indexOf(current);
  return idx >= 0 ? idx + 1 : null;
}

/** Count LinkedIn touches from the start of a step/touch list through stepIndex (inclusive). */
export function resolveLinkedInTouchOrderInSteps(steps, stepIndex) {
  if (!Array.isArray(steps) || stepIndex < 0) return null;
  let order = 0;
  for (let i = 0; i <= stepIndex && i < steps.length; i++) {
    if (isLinkedInOutboxChannel({ channel: steps[i]?.channel })) order += 1;
  }
  return order > 0 ? order : null;
}

function isLegacyPlainLinkedInChannel(source) {
  return getOutboxChannelRaw(source) === 'linkedin';
}

/**
 * Outbox / sequence plans when DIY step channel is unavailable.
 * Plain `linkedin` defaults to DM (same as DIY editor); explicit subtypes always win.
 */
export function resolveSequenceLinkedInChannel(channel) {
  const ch = getOutboxChannelRaw({ channel });
  if (ch === 'linkedin_connect' || ch === 'linkedin_dm') return ch;
  if (ch !== 'linkedin') return ch;
  return 'linkedin_dm';
}

export function getOutboxDraftMetadata(draft) {
  const meta = draft?.metadata;
  return meta && typeof meta === 'object' ? meta : {};
}

/** Display / filter key (may be static playbook slug — not valid for GET /diy-playbooks/:id). */
export function getPlaybookIdForOutboxDraft(draft) {
  if (!draft || typeof draft !== 'object') return '';
  const meta = getOutboxDraftMetadata(draft);
  return String(
    draft.playbook_id ||
      draft.playbookId ||
      draft.playbook_key ||
      draft.playbookKey ||
      meta.playbook_id ||
      meta.playbook_key ||
      meta.playbook ||
      draft.sequence?.playbook_id ||
      draft.sequence?.playbookId ||
      draft.sequence?.playbook_key ||
      draft.sequence?.playbookKey ||
      '',
  ).trim();
}

/**
 * Hoist GET /api/outbox compose fields (metadata.playbook, metadata.stage, etc.)
 * so DIY catalog + LinkedIn enrichment see the same shape as list rows.
 */
export function normalizeOutboxDraftRow(row) {
  if (!row || typeof row !== 'object') return row;
  const meta = getOutboxDraftMetadata(row);
  const out = { ...row };

  if (!out.playbook_key && !out.playbook_id) {
    const pb = meta.playbook_key || meta.playbook_id || meta.playbook;
    if (pb != null && String(pb).trim()) out.playbook_key = String(pb).trim();
  }

  if (out.stage == null && out.stage_no == null && meta.stage != null) {
    out.stage = meta.stage;
    out.stage_no = meta.stage;
  }

  if (!out.sequence_id && !out.sequenceId && meta.sequence_id) {
    out.sequence_id = meta.sequence_id;
  }

  if (!out.archetype && meta.archetype) out.archetype = meta.archetype;

  if (!out.channel && (meta.channel || meta.touch_channel)) {
    out.channel = meta.channel || meta.touch_channel;
  }

  const metaPerson = meta.person && typeof meta.person === 'object' ? meta.person : null;
  if (metaPerson) {
    out.person = { ...metaPerson, ...(out.person || {}) };
    if (!out.person_name && metaPerson.name) out.person_name = metaPerson.name;
    if (!out.linkedin_url && metaPerson.linkedin_url) out.linkedin_url = metaPerson.linkedin_url;
  }

  const metaAccount = meta.account && typeof meta.account === 'object' ? meta.account : null;
  if (metaAccount) {
    out.account = { ...metaAccount, ...(out.account || {}) };
    if (!out.account_id && metaAccount.id) out.account_id = metaAccount.id;
    if (!out.account_name && metaAccount.name) out.account_name = metaAccount.name;
  }

  if (!getOutboxChannelRaw(out)) {
    const linkedInUrl =
      out.linkedin_url || out.person?.linkedin_url || metaPerson?.linkedin_url;
    const subject = String(out.subject ?? '').trim();
    const hasBody = Boolean(
      out.html || out.body_html || out.bodyHtml || out.plain || out.body,
    );
    if (linkedInUrl && hasBody && !subject) {
      out.channel = 'linkedin';
    }
  }

  return out;
}

export function isDiyPlaybookUuid(id) {
  return DIY_PLAYBOOK_UUID_RE.test(String(id || '').trim());
}

export function isStaticOrEventPlaybookKey(key) {
  const k = String(key || '').trim();
  if (!k) return false;
  if (k.toLowerCase().startsWith('event::')) return true;
  return STATIC_LEARNWISE_PLAYBOOK_KEY_RE.test(k);
}

/** True when this outbox row is from a user-built DIY playbook (not static learnwise templates). */
export function isOutboxDraftFromDiyPlaybook(draft) {
  if (!draft || typeof draft !== 'object') return false;
  const seq = draft.sequence || {};
  if (
    draft.is_diy ||
    seq.is_diy ||
    draft.diy_playbook_id ||
    seq.diy_playbook_id
  ) {
    return true;
  }
  const source = String(draft.source || draft.message_source || seq.source || '').toLowerCase();
  if (source.includes('diy')) return true;
  const key = getPlaybookIdForOutboxDraft(draft);
  if (!key || isStaticOrEventPlaybookKey(key)) return false;
  if (key.toLowerCase().startsWith('diy_')) return true;
  return isDiyPlaybookUuid(key);
}

function normalizeOutboxPlaybookLookupKey(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^\w]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

function indexDiyPlaybookInCatalog(playbook, byId, byKey) {
  if (!playbook || typeof playbook !== 'object') return;
  const id = String(playbook.id || playbook.playbook_id || '').trim();
  if (id) byId.set(id, playbook);
  const keyFields = [
    id,
    playbook.playbook_id,
    playbook.playbook_key,
    playbook.key,
    playbook.slug,
    playbook.playbook_slug,
    playbook.playbook_name,
    playbook.name,
  ];
  for (const field of keyFields) {
    const normalized = normalizeOutboxPlaybookLookupKey(field);
    if (normalized) byKey.set(normalized, playbook);
  }
}

/** One list call — map DIY playbooks by UUID and normalized slug (avoids 404 GET by playbook_key). */
export async function loadDiyPlaybookCatalog({ tenantId, userId, apiBase }) {
  const cacheKey = `${tenantId}::${userId}`;
  if (diyPlaybookCatalogCache.has(cacheKey)) return diyPlaybookCatalogCache.get(cacheKey);

  const empty = { byId: new Map(), byKey: new Map() };
  if (!tenantId || !userId) {
    diyPlaybookCatalogCache.set(cacheKey, empty);
    return empty;
  }

  const base =
    apiBase ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'https://api.neogtm.com'
      : 'https://api.neogtm.com');

  try {
    const url = `${base}/api/diy-playbooks?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) {
      diyPlaybookCatalogCache.set(cacheKey, empty);
      return empty;
    }
    const data = await res.json();
    const list = Array.isArray(data) ? data : data?.playbooks || data?.items || [];
    const byId = new Map();
    const byKey = new Map();
    for (const p of list) {
      indexDiyPlaybookInCatalog(p, byId, byKey);
    }
    const catalog = { byId, byKey };
    diyPlaybookCatalogCache.set(cacheKey, catalog);
    return catalog;
  } catch {
    diyPlaybookCatalogCache.set(cacheKey, empty);
    return empty;
  }
}

function lookupDiyPlaybookInCatalog(draft, catalog) {
  if (!catalog) return null;
  const meta = getOutboxDraftMetadata(draft);
  const rawId = String(
    draft.diy_playbook_id ||
      draft.sequence?.diy_playbook_id ||
      draft.playbook_id ||
      draft.playbookId ||
      meta.playbook_id ||
      draft.sequence?.playbook_id ||
      '',
  ).trim();
  const rawKey = String(
    draft.playbook_key ||
      draft.playbookKey ||
      meta.playbook_key ||
      meta.playbook ||
      draft.sequence?.playbook_key ||
      rawId,
  ).trim();

  if (rawId && catalog.byId.has(rawId)) return catalog.byId.get(rawId);
  if (rawId && isDiyPlaybookUuid(rawId)) {
    const byUuid = catalog.byId.get(rawId);
    if (byUuid) return byUuid;
  }

  for (const candidate of [rawKey, rawId]) {
    const normalized = normalizeOutboxPlaybookLookupKey(candidate);
    if (normalized && catalog.byKey.has(normalized)) {
      return catalog.byKey.get(normalized);
    }
  }
  return null;
}

/** Resolve DIY playbook + steps for an outbox row (catalog + optional GET by UUID only). */
export async function resolveDiyPlaybookForOutboxDraft(
  draft,
  { tenantId, userId, apiBase, catalog = null } = {},
) {
  const cat = catalog || (await loadDiyPlaybookCatalog({ tenantId, userId, apiBase }));
  const row = lookupDiyPlaybookInCatalog(draft, cat);
  if (!row) return null;

  const steps = row.steps || row.playbook_steps;
  if (Array.isArray(steps) && steps.length > 0) {
    return { id: row.id || row.playbook_id, steps };
  }

  const uuid = String(row.id || row.playbook_id || '').trim();
  if (!isDiyPlaybookUuid(uuid)) return null;

  const failKey = `${tenantId}::${uuid}`;
  if (diyPlaybookFetchFailedKeys.has(failKey)) return null;

  return fetchDiyPlaybookSteps(uuid, { tenantId, userId, apiBase });
}

function isExplicitLinkedInSubtype(channel) {
  const ch = getOutboxChannelRaw({ channel });
  return ch === 'linkedin_connect' || ch === 'linkedin_dm';
}

/** DIY playbook step channel for this outbox step_id (source of truth for DM vs connect). */
export function resolveLinkedInChannelFromDiyPlaybook(draft, diyPlaybook) {
  const steps = Array.isArray(diyPlaybook)
    ? diyPlaybook
    : diyPlaybook?.steps || diyPlaybook?.playbook_steps;
  if (!Array.isArray(steps) || !steps.length) return null;

  const stepId = getDraftStepId(draft);
  if (!stepId) return null;

  const step = steps.find((s) => {
    const id = String(s?.id ?? s?.step_id ?? '').trim();
    return id === stepId;
  });
  if (!step || !isLinkedInOutboxChannel({ channel: step.channel })) return null;

  return channelFromDiyStep(step, steps);
}

export async function fetchDiyPlaybookSteps(playbookId, { tenantId, userId, apiBase }) {
  const pid = String(playbookId || '').trim();
  if (!pid || !tenantId || !userId) return null;
  if (!isDiyPlaybookUuid(pid)) return null;

  const cacheKey = `${tenantId}::${pid}`;
  if (diyPlaybookStepsCache.has(cacheKey)) return diyPlaybookStepsCache.get(cacheKey);

  const base =
    apiBase ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'https://api.neogtm.com'
      : 'https://api.neogtm.com');

  try {
    const url = `${base}/api/diy-playbooks/${encodeURIComponent(pid)}?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) {
      diyPlaybookStepsCache.set(cacheKey, null);
      if (res.status === 404) diyPlaybookFetchFailedKeys.add(cacheKey);
      return null;
    }
    const data = await res.json();
    const steps = data?.steps || data?.playbook?.steps || [];
    const payload = { id: pid, steps: Array.isArray(steps) ? steps : [] };
    diyPlaybookStepsCache.set(cacheKey, payload);
    return payload;
  } catch {
    diyPlaybookStepsCache.set(cacheKey, null);
    return null;
  }
}

/** @deprecated Use enrichOutboxDraftsWithSequencePlans — template position ignores sent/deleted steps. */
export function annotateLinkedInTouchOrderInSequence(rows) {
  return rows;
}

/** Shallow clone before sequence-plan enrichment (no template-based LinkedIn order). */
export function withLinkedInSequenceAnnotation(rows) {
  if (!Array.isArray(rows)) return rows;
  return rows.map((r) => ({ ...r }));
}

function getTouchStageFromPlanTouch(touch) {
  return Number(touch?.touch_number ?? touch?.stage_no ?? touch?.stage ?? touch?.order ?? 0);
}

function getTouchChannelFromPlanTouch(touch) {
  if (!touch || typeof touch !== 'object') return '';
  const nested = touch.step || touch.template_step || touch.playbook_step;
  return (
    touch.channel ??
    touch.touch_channel ??
    touch.step_channel ??
    touch.linkedin_channel ??
    touch.linkedinChannel ??
    nested?.channel ??
    nested?.step_channel ??
    ''
  );
}

/** Legacy plain `linkedin` on a DIY step: first LinkedIn slot in playbook order → connect. */
function channelFromDiyStep(step, steps) {
  if (!step) return null;
  const resolved = resolveDiyStepChannelFromApiStep(step);
  const raw = getOutboxChannelRaw({ channel: resolved });
  if (raw === 'linkedin_connect' || raw === 'linkedin_dm') return raw;
  if (raw === 'linkedin') {
    if (Array.isArray(steps) && steps.length) {
      const sorted = sortDiyStepsByOrder(steps);
      const linkedinSteps = sorted.filter((s) =>
        isLinkedInOutboxChannel({ channel: s?.channel }),
      );
      const stepId = String(step?.id ?? step?.step_id ?? '').trim();
      const idx = linkedinSteps.findIndex(
        (s) => String(s?.id ?? s?.step_id ?? '').trim() === stepId,
      );
      if (idx === 0) return 'linkedin_connect';
      if (idx > 0) return 'linkedin_dm';
    }
    return 'linkedin_dm';
  }
  return null;
}

/** Playbook step UUID — never use ad-hoc `draft-*` outbox ids for DIY/plan lookup. */
export function getDraftStepId(draft) {
  const explicit = String(draft?.stepId ?? draft?.step_id ?? '').trim();
  if (explicit) return explicit;
  const id = String(draft?.id ?? '').trim();
  if (!id || id.startsWith('draft-')) return '';
  return id;
}

/** Search all DIY playbooks in catalog for this outbox step UUID. */
export function resolveLinkedInChannelFromDiyCatalogByStepId(stepId, catalog) {
  const sid = String(stepId || '').trim();
  if (!sid || !catalog) return null;
  const seen = new Set();
  for (const playbook of catalog.byId.values()) {
    const pbId = String(playbook?.id || playbook?.playbook_id || '').trim();
    if (pbId && seen.has(pbId)) continue;
    if (pbId) seen.add(pbId);
    const steps = playbook?.steps || playbook?.playbook_steps;
    if (!Array.isArray(steps)) continue;
    const step = steps.find((s) => String(s?.id ?? s?.step_id ?? '').trim() === sid);
    if (!step) continue;
    const ch = channelFromDiyStep(step, steps);
    if (ch) return ch;
  }
  return null;
}

export function findPlanTouchForDraft(draft, planTouches) {
  if (!Array.isArray(planTouches) || !planTouches.length) return null;
  const stepId = getDraftStepId(draft);
  const stage = getOutboxStageNo(draft);
  if (stepId) {
    const byStep = planTouches.find((t) => getTouchStepIdFromPlanTouch(t) === stepId);
    if (byStep) return byStep;
  }
  if (stage > 0) {
    const byStage = planTouches.find((t) => getTouchStageFromPlanTouch(t) === stage);
    if (byStage) return byStage;
  }
  return null;
}

/** Channel from GET /sequences/:id/touches template rows (before status/outreach merge). */
export function resolveLinkedInChannelFromPlanTouch(draft, planTouches) {
  const touch = findPlanTouchForDraft(draft, planTouches);
  if (!touch) return null;
  const raw = getOutboxChannelRaw({ channel: getTouchChannelFromPlanTouch(touch) });
  if (raw === 'linkedin_connect' || raw === 'linkedin_dm') return raw;
  return null;
}

async function hydrateDiyCatalogStepsForStepLookup(catalog, { tenantId, userId, apiBase }) {
  if (!catalog?.byId?.size) return;
  const missing = [...catalog.byId.values()].filter(
    (p) => !Array.isArray(p?.steps) || !p.steps.length,
  );
  const concurrency = 6;
  for (let i = 0; i < missing.length; i += concurrency) {
    await Promise.all(
      missing.slice(i, i + concurrency).map(async (row) => {
        const uuid = String(row?.id || row?.playbook_id || '').trim();
        if (!isDiyPlaybookUuid(uuid)) return;
        const full = await fetchDiyPlaybookSteps(uuid, { tenantId, userId, apiBase });
        if (full?.steps?.length) row.steps = full.steps;
      }),
    );
  }
}

export async function resolveDiyLinkedInChannelForDraft(
  draft,
  { tenantId, userId, apiBase, catalog = null } = {},
) {
  const stepId = getDraftStepId(draft);
  const cat =
    catalog || (await loadDiyPlaybookCatalog({ tenantId, userId, apiBase }));
  if (stepId) {
    let fromCatalog = resolveLinkedInChannelFromDiyCatalogByStepId(stepId, cat);
    if (!fromCatalog && cat?.byId?.size) {
      await hydrateDiyCatalogStepsForStepLookup(cat, { tenantId, userId, apiBase });
      fromCatalog = resolveLinkedInChannelFromDiyCatalogByStepId(stepId, cat);
    }
    if (fromCatalog) return fromCatalog;
  }
  const playbook = await resolveDiyPlaybookForOutboxDraft(draft, {
    tenantId,
    userId,
    apiBase,
    catalog: cat,
  });
  return resolveLinkedInChannelFromDiyPlaybook(draft, playbook);
}

export function getSequencePlanFromCache(sequenceId, personId) {
  const key = sequencePlanCacheKey(sequenceId, personId);
  const entry = sequenceTouchPlanCache.get(key);
  if (!entry) return { merged: [], plan: [] };
  if (Array.isArray(entry)) return { merged: entry, plan: [] };
  return {
    merged: Array.isArray(entry.merged) ? entry.merged : [],
    plan: Array.isArray(entry.plan) ? entry.plan : [],
  };
}

/** Person already connected on LinkedIn — further outreach should be DMs. */
export function personAlreadyLinkedInConnected(source) {
  const row = typeof source === 'object' && source !== null ? source : {};
  const p = row.person || row;
  const v = p?.linkedin_connected ?? row.linkedin_connected;
  return v === true || v === 1 || v === 'true' || v === '1';
}

/** LinkedIn touches that actually completed (sent/deleted-after-send do not count if never sent). */
export function touchWasExecutedInSequence(touch) {
  if (!touch || typeof touch !== 'object') return false;
  if (
    touch.actual_sent_at ||
    touch.sent_at ||
    touch.sentAt ||
    touch.sent_at_utc ||
    touch.completed_at ||
    touch.completedAt ||
    touch.manual_completed_at ||
    touch.marked_done_at ||
    touch.last_sent_at ||
    touch.lastSentAt
  ) {
    return true;
  }
  if (touch.is_sent === true || touch.was_sent === true) return true;
  const status = String(touch.status ?? touch.step_status ?? touch.state ?? '').toLowerCase();
  if (
    ['sent', 'manual_completion', 'completed', 'delivered', 'done', 'finished'].includes(status)
  ) {
    return true;
  }
  if (status.includes('sent') || status.includes('complete')) return true;
  return false;
}

function getTouchStepIdFromPlanTouch(touch) {
  return String(touch?.step_id ?? touch?.stepId ?? touch?.id ?? '').trim();
}

/** Ordered LinkedIn slots in the sequence template (not outbox "next step = 1" index). */
export function buildLinkedInTimeline(touches) {
  if (!Array.isArray(touches)) return [];
  return touches
    .map((t) => ({
      stage: getTouchStageFromPlanTouch(t),
      executed: touchWasExecutedInSequence(t),
      stepId: getTouchStepIdFromPlanTouch(t),
      channel: getTouchChannelFromPlanTouch(t),
      raw: t,
    }))
    .filter((t) => t.stage > 0 && isLinkedInOutboxChannel({ channel: t.channel }))
    .sort((a, b) => a.stage - b.stage);
}

/**
 * Resolve this draft's position among LinkedIn touches using step_id + template stages.
 * Outbox rows often use stage_no=1 for "next due" even when template touch_number is 3+.
 */
export function inferLinkedInTouchOrderForDraft(touches, draft) {
  if (!draft || !isLinkedInOutboxChannel(draft)) return null;

  const timeline = buildLinkedInTimeline(touches);
  if (!timeline.length) return null;

  const draftStepId = String(draft.stepId ?? draft.id ?? draft.step_id ?? '').trim();
  const draftStage = getOutboxStageNo(draft);

  let currentIdx = -1;
  if (draftStepId) {
    currentIdx = timeline.findIndex((t) => t.stepId && t.stepId === draftStepId);
  }

  if (currentIdx < 0) {
    currentIdx = timeline.findIndex((t) => t.stage === draftStage && !t.executed);
  }

  if (currentIdx < 0) {
    currentIdx = timeline.findIndex((t) => t.stage === draftStage);
  }

  // stage_no=1 on outbox row = next-up pointer → first pending LinkedIn in template order
  if (currentIdx < 0 && draftStage === 1) {
    currentIdx = timeline.findIndex((t) => !t.executed);
  }

  if (currentIdx < 0) {
    const pending = timeline.filter((t) => !t.executed);
    if (pending.length === 1) {
      currentIdx = timeline.indexOf(pending[0]);
    }
  }

  if (currentIdx < 0) return null;
  return currentIdx + 1;
}

export function isLinkedInChannelOnPlanTouch(touch) {
  return isLinkedInOutboxChannel({ channel: getTouchChannelFromPlanTouch(touch) });
}

/** @deprecated Prefer inferLinkedInTouchOrderForDraft — draft.stage_no is often "next up" (1), not template touch #. */
export function countPriorExecutedLinkedInTouches(touches, currentStage) {
  const order = inferLinkedInTouchOrderFromSequenceTouches(touches, currentStage, {
    assumeLinkedInAtStage: true,
  });
  return order != null ? order - 1 : 0;
}

export function inferLinkedInTouchOrderFromSequenceTouches(
  touches,
  currentStage,
  { assumeLinkedInAtStage = false, draft = null } = {},
) {
  if (draft) return inferLinkedInTouchOrderForDraft(touches, draft);
  const pseudo = assumeLinkedInAtStage ? { channel: 'linkedin', stage_no: currentStage } : null;
  if (pseudo) return inferLinkedInTouchOrderForDraft(touches, pseudo);
  return null;
}

function applyLinkedInOrderFromSequencePlan(
  draft,
  touches,
  {
    extraPriorExecuted = 0,
    diyPlaybook = null,
    planTouches = null,
    diyChannelResolved = null,
  } = {},
) {
  const planResolved = Array.isArray(planTouches)
    ? resolveLinkedInChannelFromPlanTouch(draft, planTouches)
    : null;
  const diyFromPlaybook =
    diyChannelResolved ?? resolveLinkedInChannelFromDiyPlaybook(draft, diyPlaybook);

  if (personAlreadyLinkedInConnected(draft)) {
    return {
      ...draft,
      __linkedinTouchOrder: 2,
      __linkedinPriorExecuted: 1,
      __linkedinSkipConnectReason: 'person_already_connected',
      __diyLinkedInChannel: 'linkedin_dm',
    };
  }

  const timeline = buildLinkedInTimeline(touches);
  let order = inferLinkedInTouchOrderForDraft(touches, draft);
  const extraPrior = Number(extraPriorExecuted) || 0;

  if (order == null && extraPrior > 0) {
    order = extraPrior + 1;
  }
  if (order == null) {
    const earlyChannel = diyFromPlaybook ?? planResolved;
    if (earlyChannel) {
      return {
        ...draft,
        ...(planResolved ? { __planTemplateLinkedInChannel: planResolved } : {}),
        ...(diyFromPlaybook ? { __diyPlaybookLinkedInChannel: diyFromPlaybook } : {}),
        __diyLinkedInChannel: earlyChannel,
      };
    }
    return draft;
  }

  order = Math.max(order, extraPrior + 1);

  const draftStepId = String(draft.stepId ?? draft.id ?? draft.step_id ?? '').trim();
  let currentIdx = order - 1;
  if (draftStepId) {
    const byStep = timeline.findIndex((t) => t.stepId && t.stepId === draftStepId);
    if (byStep >= 0) currentIdx = byStep;
  }

  let priorExecuted = 0;
  for (let i = 0; i < currentIdx; i++) {
    if (timeline[i]?.executed) priorExecuted += 1;
  }
  priorExecuted = Math.max(priorExecuted, extraPrior);

  const templateStage =
    currentIdx >= 0 && timeline[currentIdx] ? timeline[currentIdx].stage : getOutboxStageNo(draft);

  const templateEntry = currentIdx >= 0 ? timeline[currentIdx] : null;
  const touchOrder = currentIdx + 1;
  const templateRaw = templateEntry?.channel ?? '';
  const templateResolved =
    diyFromPlaybook ??
    planResolved ??
    (templateEntry ? resolveSequenceLinkedInChannel(templateRaw) : null);
  const draftRaw = getOutboxChannelRaw(draft);
  const hasExplicitSubtype =
    draftRaw === 'linkedin_connect' || draftRaw === 'linkedin_dm';

  const channelForAnnot =
    templateResolved && !hasExplicitSubtype ? templateResolved : null;

  return {
    ...draft,
    __linkedinTouchOrder: touchOrder,
    __linkedinPriorExecuted: priorExecuted,
    __linkedinTimelineIndex: touchOrder,
    __linkedinTemplateStage: templateStage,
    ...(templateRaw ? { __linkedinTemplateChannelRaw: templateRaw } : {}),
    ...(planResolved ? { __planTemplateLinkedInChannel: planResolved } : {}),
    ...(diyFromPlaybook ? { __diyPlaybookLinkedInChannel: diyFromPlaybook } : {}),
    ...(channelForAnnot ? { __diyLinkedInChannel: channelForAnnot } : {}),
  };
}

/** Walk /api/outbox/previous-touch to count executed LinkedIn steps before current stage. */
export async function countPriorExecutedLinkedInViaPreviousTouch(
  sequenceId,
  currentStage,
  { tenantId, userId, apiBase },
) {
  const stageStart = Number(currentStage);
  if (!sequenceId || !tenantId || !userId || !Number.isFinite(stageStart) || stageStart <= 1) {
    return 0;
  }

  const base =
    apiBase ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'https://api.neogtm.com'
      : 'https://api.neogtm.com');

  let count = 0;
  let stage = stageStart;
  const visited = new Set();

  for (let i = 0; i < 24 && stage > 1; i++) {
    if (visited.has(stage)) break;
    visited.add(stage);

    try {
      const url = `${base}/api/outbox/previous-touch?tenant_id=${encodeURIComponent(tenantId)}&sequence_id=${encodeURIComponent(sequenceId)}&stage_no=${stage}&user_id=${encodeURIComponent(userId)}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) break;
      const data = await res.json();
      const prev = data?.touch;
      if (!prev) break;

      if (isLinkedInOutboxChannel(prev) && touchWasExecutedInSequence(prev)) {
        count += 1;
      }

      const prevStage = getTouchStageFromPlanTouch(prev);
      if (!prevStage || prevStage >= stage) break;
      stage = prevStage;
    } catch {
      break;
    }
  }

  return count;
}

/** Apply LinkedIn sequence context to a single draft (e.g. Outbox drawer). */
export function enrichDraftLinkedInContext(
  draft,
  _peerDrafts,
  sequenceTouches,
  {
    extraPriorExecuted = 0,
    diyPlaybook = null,
    planTouches = null,
    diyChannelResolved = null,
  } = {},
) {
  const base = normalizeOutboxDraftRow(draft);
  if (!base || !isLinkedInOutboxChannel(base)) return base;

  const plan = Array.isArray(planTouches) ? planTouches : [];
  const diyChannel =
    diyChannelResolved ?? resolveLinkedInChannelFromDiyPlaybook(base, diyPlaybook);

  if (diyChannel && (!Array.isArray(sequenceTouches) || sequenceTouches.length === 0)) {
    if (extraPriorExecuted > 0) {
      return applyLinkedInOrderFromSequencePlan(base, [], {
        extraPriorExecuted,
        planTouches: plan,
        diyChannelResolved: diyChannel,
      });
    }
    return {
      ...base,
      __diyPlaybookLinkedInChannel: diyChannel,
      __diyLinkedInChannel: diyChannel,
    };
  }

  if (!Array.isArray(sequenceTouches) || sequenceTouches.length === 0) {
    if (extraPriorExecuted > 0) {
      const touchOrder = extraPriorExecuted + 1;
      return {
        ...base,
        __linkedinTouchOrder: touchOrder,
        __linkedinPriorExecuted: extraPriorExecuted,
        __diyLinkedInChannel: resolveSequenceLinkedInChannel(
          base.__linkedinTemplateChannelRaw ?? getOutboxChannelRaw(base),
        ),
      };
    }
    return base;
  }
  return applyLinkedInOrderFromSequencePlan(base, sequenceTouches, {
    extraPriorExecuted,
    planTouches: plan,
    diyChannelResolved: diyChannel,
  });
}

function sequencePlanCacheKey(sequenceId, personId) {
  return `${sequenceId}::${personId}`;
}

function mergeTouchChannelFields(prevTouch, nextTouch) {
  const prevCh = getTouchChannelFromPlanTouch(prevTouch);
  const nextCh = getTouchChannelFromPlanTouch(nextTouch);
  if (isExplicitLinkedInSubtype(prevCh)) return prevCh;
  if (isExplicitLinkedInSubtype(nextCh)) return nextCh;
  return nextCh || prevCh;
}

/** Merge status/outreach history, then overlay sequence template touches (template channel wins). */
function mergeTouchPlansByStage(planTouches, ...activityLists) {
  const byStage = new Map();
  for (const list of activityLists) {
    if (!Array.isArray(list)) continue;
    for (const t of list) {
      const st = getTouchStageFromPlanTouch(t);
      if (st <= 0) continue;
      const prev = byStage.get(st);
      if (!prev) {
        byStage.set(st, { ...t });
        continue;
      }
      const channel = mergeTouchChannelFields(prev, t);
      byStage.set(st, { ...prev, ...t, channel, touch_channel: channel, step_channel: channel });
    }
  }
  for (const t of planTouches || []) {
    const st = getTouchStageFromPlanTouch(t);
    if (st <= 0) continue;
    const prev = byStage.get(st);
    const planCh = getTouchChannelFromPlanTouch(t);
    if (!prev) {
      byStage.set(st, { ...t });
      continue;
    }
    const channel = isExplicitLinkedInSubtype(planCh)
      ? planCh
      : mergeTouchChannelFields(prev, { channel: planCh });
    byStage.set(st, {
      ...prev,
      ...t,
      channel,
      touch_channel: channel,
      step_channel: channel,
    });
  }
  return [...byStage.values()].sort(
    (a, b) => getTouchStageFromPlanTouch(a) - getTouchStageFromPlanTouch(b),
  );
}

/** [2026-07-01] BE resolver was upgraded to handle UUID / UUIDv5 /
 *  ObjectId / slug via format-aware branch + email fallback
 *  (analyticsV2Routes.ts:1856). The old regex-shape gate now blocks
 *  personas with slug person_ids (evasys, scantron), causing the
 *  Interactions badge to drop from 2 to 0 the moment the tab opens
 *  (Danny Reilly case). Only reject truly empty ids.
 */
export function canUseAnalyticsV2AllOutreach(personId) {
  return String(personId || '').trim().length > 0;
}

async function fetchPersonOutreachTouchHistory(personId, { tenantId, userId, apiBase }) {
  if (!canUseAnalyticsV2AllOutreach(personId)) return [];
  const base =
    apiBase ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'https://api.neogtm.com'
      : 'https://api.neogtm.com');
  try {
    const url = `${base}/api/analytics-v2/personas/${encodeURIComponent(personId)}/all-outreach?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) return [];
    const data = await res.json();
    const rows = Array.isArray(data) ? data : data?.messages || data?.data || [];
    return rows.map((msg) => ({
      touch_number: msg.stage_no ?? msg.touch_number ?? msg.stage,
      stage_no: msg.stage_no ?? msg.touch_number,
      channel: msg.channel,
      actual_sent_at: msg.sent_at ?? msg.completed_at,
      status: msg.status || 'sent',
      step_id: msg.step_id ?? msg.stepId ?? msg.id,
    }));
  } catch {
    return [];
  }
}

async function fetchPlaybookStatusTouchHistory(personId, { tenantId, userId, apiBase }) {
  const base =
    apiBase ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'https://api.neogtm.com'
      : 'https://api.neogtm.com');
  try {
    const url = `${base}/api/personas/${encodeURIComponent(personId)}/playbook-status?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) return [];
    const data = await res.json();
    const progress = data?.progress || data;
    return [
      ...(progress?.completed_touches || []),
      ...(data?.completed_touches || []),
      ...(progress?.upcoming_touches || []),
      ...(data?.upcoming_touches || []),
    ];
  } catch {
    return [];
  }
}

export async function fetchSequenceTouchPlan(sequenceId, personId, { tenantId, userId, apiBase }) {
  const key = sequencePlanCacheKey(sequenceId, personId);
  if (sequenceTouchPlanCache.has(key)) return sequenceTouchPlanCache.get(key);

  const base =
    apiBase ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'https://api.neogtm.com'
      : 'https://api.neogtm.com');

  let planTouches = [];
  try {
    const url = `${base}/api/sequences/${encodeURIComponent(sequenceId)}/touches?person_id=${encodeURIComponent(personId)}&tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}`;
    const res = await fetch(url, { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      planTouches = data?.touches || data?.data?.touches || (Array.isArray(data) ? data : []);
    }
  } catch {
    planTouches = [];
  }

  const statusTouches = await fetchPlaybookStatusTouchHistory(personId, {
    tenantId,
    userId,
    apiBase,
  });
  const outreachTouches = canUseAnalyticsV2AllOutreach(personId)
    ? await fetchPersonOutreachTouchHistory(personId, { tenantId, userId, apiBase })
    : [];
  const merged = mergeTouchPlansByStage(planTouches, statusTouches, outreachTouches);
  sequenceTouchPlanCache.set(key, { merged, plan: planTouches });
  return merged;
}

/** Resolve LinkedIn touch order using sequence touch plans (Outbox list load). */
export async function enrichOutboxDraftsWithSequencePlans(drafts, { tenantId, userId, apiBase }) {
  if (!Array.isArray(drafts) || !tenantId || !userId) return drafts;

  const copy = drafts.map((d) => normalizeOutboxDraftRow({ ...d }));
  const keysNeeded = new Map();
  const hasLinkedIn = copy.some((d) => isLinkedInOutboxChannel(d));

  for (const d of copy) {
    if (!isLinkedInOutboxChannel(d)) continue;
    const seqId = d.sequence_id || d.sequenceId || d.sequence?.id;
    const personId =
      d.person_id || d.personId || d.person?.person_id || d.person?.id || '';
    if (seqId && personId) {
      const key = sequencePlanCacheKey(seqId, personId);
      if (!keysNeeded.has(key)) keysNeeded.set(key, { seqId, personId });
    }
  }

  const entries = [...keysNeeded.values()];
  const concurrency = 8;
  for (let i = 0; i < entries.length; i += concurrency) {
    await Promise.all(
      entries.slice(i, i + concurrency).map(({ seqId, personId }) =>
        fetchSequenceTouchPlan(seqId, personId, { tenantId, userId, apiBase }),
      ),
    );
  }

  const diyCatalog = hasLinkedIn
    ? await loadDiyPlaybookCatalog({ tenantId, userId, apiBase })
    : null;

  const diyChannelByStepId = new Map();
  for (const d of copy) {
    if (!isLinkedInOutboxChannel(d)) continue;
    const stepId = getDraftStepId(d);
    if (!stepId || diyChannelByStepId.has(stepId)) continue;
    const ch = await resolveDiyLinkedInChannelForDraft(d, {
      tenantId,
      userId,
      apiBase,
      catalog: diyCatalog,
    });
    if (ch) diyChannelByStepId.set(stepId, ch);
  }

  for (const d of copy) {
    if (!isLinkedInOutboxChannel(d)) continue;
    const seqId = d.sequence_id || d.sequenceId || d.sequence?.id;
    const personId =
      d.person_id || d.personId || d.person?.person_id || d.person?.id || '';
    const { merged: touches, plan: planTouches } =
      seqId && personId
        ? getSequencePlanFromCache(seqId, personId)
        : { merged: [], plan: [] };
    const stepId = getDraftStepId(d);
    const enriched = applyLinkedInOrderFromSequencePlan(d, touches || [], {
      planTouches,
      diyChannelResolved: diyChannelByStepId.get(stepId) ?? null,
    });
    Object.assign(d, enriched);
  }

  return copy;
}

function outboxBodyTextForChannelHint(row) {
  const parts = [
    row?.html,
    row?.body_html,
    row?.bodyHtml,
    row?.plain,
    row?.body,
    row?.subject,
  ];
  return parts.filter((p) => p != null && String(p).trim()).join(' ');
}

function outboxBodyTextIndicatesConnectionRequest(row) {
  const text = outboxBodyTextForChannelHint(row).toLowerCase();
  if (!text) return false;
  const hints = [
    'would be great to connect',
    'like to connect',
    'request to connect',
    'connection request',
    'invite you to connect',
  ];
  return hints.some((h) => text.includes(h));
}

function primaryChannelValue(row) {
  for (const key of PRIMARY_CHANNEL_KEYS) {
    const v = row?.[key];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  const nested =
    readNestedChannel(row, 'step.channel') ||
    readNestedChannel(row, 'touch.channel') ||
    readNestedChannel(row, 'metadata.channel') ||
    readNestedChannel(row, 'metadata.touch_channel');
  if (nested) return nested;
  return '';
}

function secondaryValues(row) {
  const out = [];
  for (const key of SECONDARY_CHANNEL_KEYS) {
    const v = row?.[key];
    if (v == null || v === '') continue;
    out.push(String(v).trim().toLowerCase());
  }
  return out;
}

function textIndicatesConnectionRequest(...values) {
  const hints = [
    'linkedin_connect',
    'connection_request',
    'connect_request',
    'connection request',
    'connect_only',
  ];
  return values.some((raw) => {
    const v = String(raw ?? '').trim().toLowerCase();
    if (!v) return false;
    return hints.some((h) => v === h || v.includes(h));
  });
}

/** Raw channel on an outbox row / draft (BE may use linkedin_connect | linkedin_dm | linkedin). */
export function getOutboxChannelRaw(source) {
  const row = typeof source === 'object' && source !== null ? source : { channel: source };
  return primaryChannelValue(row).toLowerCase();
}

export function isLinkedInOutboxChannel(source) {
  const row = typeof source === 'object' && source !== null ? source : { channel: source };
  const ch = getOutboxChannelRaw(source);
  if (
    ch === 'linkedin' ||
    ch === 'linkedin_dm' ||
    ch === 'linkedin_connect' ||
    ch.startsWith('linkedin_')
  ) {
    return true;
  }
  return secondaryValues(row).some(
    (v) => v === 'linkedin' || v.startsWith('linkedin_') || v.includes('linkedin'),
  );
}

/**
 * LinkedIn subtype for outbox / preview rows — aligned with DIY (`linkedin_dm` | `linkedin_connect`).
 * Uses explicit row channel, sequence template annotation, playbook step list, then DIY defaults.
 */
export function resolveOutboxLinkedInChannel(source) {
  const row = typeof source === 'object' && source !== null ? source : { channel: source };
  const ch = getOutboxChannelRaw(row);
  if (ch === 'linkedin_connect' || ch === 'linkedin_dm') return ch;

  const diyPlaybookChannel =
    row.__diyPlaybookLinkedInChannel ?? row.diyPlaybookLinkedInChannel;
  if (diyPlaybookChannel === 'linkedin_connect' || diyPlaybookChannel === 'linkedin_dm') {
    return diyPlaybookChannel;
  }

  const planChannel = row.__planTemplateLinkedInChannel ?? row.planTemplateLinkedInChannel;
  if (planChannel === 'linkedin_connect' || planChannel === 'linkedin_dm') {
    return planChannel;
  }

  const templateRaw = row.__linkedinTemplateChannelRaw ?? row.linkedinTemplateChannelRaw;
  if (templateRaw != null && String(templateRaw).trim() !== '') {
    return resolveSequenceLinkedInChannel(templateRaw);
  }

  const nested =
    readNestedChannel(row, 'step.channel') ||
    readNestedChannel(row, 'touch.channel') ||
    readNestedChannel(row, 'sequence.channel');
  if (nested && isLinkedInOutboxChannel({ channel: nested })) {
    return resolveSequenceLinkedInChannel(nested);
  }

  // DIY editor / playbook preview: explicit step channel only (no touch-order inference).
  if (Array.isArray(row.allSteps) && row.stepIndex != null) {
    const step = row.allSteps[row.stepIndex];
    if (step && isLinkedInOutboxChannel({ channel: step.channel })) {
      return resolveDiyStepLinkedInChannel(step.channel);
    }
  }

  if (Array.isArray(row.allSteps) && row.touch) {
    const touch = row.touch;
    if (isLinkedInOutboxChannel({ channel: touch.channel })) {
      return resolveDiyStepLinkedInChannel(touch.channel);
    }
  }

  const subType = String(
    row.linkedin_sub_type ?? row.linkedinSubType ?? row.linkedin_message_type ?? '',
  )
    .trim()
    .toLowerCase();
  if (subType === 'connect' || subType === 'connection' || subType === 'connection_request') {
    return 'linkedin_connect';
  }
  if (subType === 'dm' || subType === 'direct' || subType === 'direct_message') {
    return 'linkedin_dm';
  }

  if (ch.includes('connection_request') || ch.includes('connect_request')) return 'linkedin_connect';

  const secondary = secondaryValues(row);
  if (textIndicatesConnectionRequest(ch, ...secondary)) return 'linkedin_connect';

  if (row.is_connection_request === true || row.isConnectionRequest === true) {
    return 'linkedin_connect';
  }

  if (personAlreadyLinkedInConnected(row)) return 'linkedin_dm';

  const annotated = row.__diyLinkedInChannel ?? row.diyLinkedInChannel;
  if (annotated) {
    return resolveSequenceLinkedInChannel(
      isLegacyPlainLinkedInChannel(row) ? 'linkedin' : annotated,
    );
  }

  if (isLegacyPlainLinkedInChannel(row) && outboxBodyTextIndicatesConnectionRequest(row)) {
    return 'linkedin_connect';
  }

  if (isLegacyPlainLinkedInChannel(row)) {
    return resolveSequenceLinkedInChannel('linkedin');
  }

  return ch;
}

export function isLinkedInConnectionRequest(source) {
  if (!isLinkedInOutboxChannel(source)) return false;
  return resolveOutboxLinkedInChannel(source) === 'linkedin_connect';
}

/** Short label for LinkedIn sub-type badges (drawer / list). */
export function getLinkedInOutboxSubtypeLabel(source) {
  if (!isLinkedInOutboxChannel(source)) return null;
  const resolved = resolveOutboxLinkedInChannel(source);
  if (resolved === 'linkedin_connect') return 'Connection request';
  if (resolved === 'linkedin_dm') return 'Direct message';
  return null;
}

/** Wire value for `linkedin_sub_type` on send / analytics. */
export function getOutboxLinkedInSubTypeWireValue(source) {
  const resolved = resolveOutboxLinkedInChannel(source);
  if (resolved === 'linkedin_connect') return 'connect';
  if (resolved === 'linkedin_dm') return 'dm';
  return undefined;
}

/**
 * Channel fields for POST /api/outbox/:id/send and analytics.
 * Uses enriched draft (`__diyPlaybookLinkedInChannel`, plan template, etc.).
 */
export function buildOutboxChannelRecordingPayload(source) {
  const row = typeof source === 'object' && source !== null ? source : { channel: source };
  if (!isLinkedInOutboxChannel(row)) {
    return { channel: getOutboxChannelRaw(row) || 'email' };
  }
  const channel = resolveOutboxLinkedInChannel(row);
  const linkedin_sub_type = getOutboxLinkedInSubTypeWireValue(row);
  const payload = { channel };
  if (linkedin_sub_type) {
    payload.linkedin_sub_type = linkedin_sub_type;
    payload.linkedinSubType = linkedin_sub_type;
  }
  if (channel === 'linkedin_connect') {
    payload.is_connection_request = true;
  }
  return payload;
}

export function isEmailOutboxChannel(source) {
  return getOutboxChannelRaw(source) === 'email';
}

export function isCallOutboxChannel(source) {
  return getOutboxChannelRaw(source) === 'call';
}

/** Human-readable channel line (Outbox list, playbook touch preview, etc.). */
export function getChannelDisplayLabel(source) {
  if (isEmailOutboxChannel(source)) return 'Email';
  if (isCallOutboxChannel(source)) return 'Call';
  if (isLinkedInOutboxChannel(source)) {
    const sub = getLinkedInOutboxSubtypeLabel(source);
    return sub ? `LinkedIn — ${sub}` : 'LinkedIn';
  }
  const ch = getOutboxChannelRaw(source);
  return ch ? ch.replace(/_/g, ' ') : 'Message';
}

/** List headline: LinkedIn has no subject line — show the channel label, not "No subject". */
export function getOutboxDraftHeadline(draft, { isGenerating = false } = {}) {
  if (isGenerating) return 'Generating...';
  const subject = String(draft?.subject || '').trim();
  if (subject) return subject;
  if (isLinkedInOutboxChannel(draft)) {
    return getLinkedInOutboxSubtypeLabel(draft)
      || (isLinkedInConnectionRequest(draft) ? 'Connection request' : 'Direct message');
  }
  const preview = String(draft?.message_preview || '').trim();
  return preview || 'No subject';
}

/**
 * DIY step channel: legacy `linkedin` → linkedin_dm unless user picked linkedin_connect.
 * Touch order does not force connection request — that is an explicit picker choice.
 */
export function resolveDiyStepLinkedInChannel(channel) {
  const ch = getOutboxChannelRaw({ channel });
  if (ch === 'linkedin_connect' || ch === 'linkedin_dm') return ch;
  if (ch !== 'linkedin') {
    return channel != null && String(channel).trim() !== '' ? String(channel).trim() : 'email';
  }
  return 'linkedin_dm';
}

/**
 * Rehydrate DIY form channel from GET /diy-playbooks step rows.
 * BE may persist `channel: "linkedin"` + `channel_subtype: "linkedin_connect"|"linkedin_dm"` (Fix #52b).
 */
export function resolveDiyStepChannelFromApiStep(step) {
  const row = step && typeof step === 'object' ? step : { channel: step };
  const primary = getOutboxChannelRaw({ channel: row.channel });
  if (primary === 'linkedin_connect' || primary === 'linkedin_dm') {
    return primary;
  }
  const subtypeRaw = getOutboxChannelRaw({
    channel:
      row.channel_subtype ??
      row.channelSubtype ??
      row.linkedin_channel_subtype ??
      row.linkedinChannelSubtype,
  });
  if (subtypeRaw === 'linkedin_connect' || subtypeRaw === 'linkedin_dm') {
    return subtypeRaw;
  }
  const subWire = String(
    row.linkedin_sub_type ?? row.linkedinSubType ?? '',
  )
    .trim()
    .toLowerCase();
  if (
    subWire === 'connect' ||
    subWire === 'connection' ||
    subWire === 'connection_request'
  ) {
    return 'linkedin_connect';
  }
  if (subWire === 'dm' || subWire === 'direct' || subWire === 'direct_message') {
    return 'linkedin_dm';
  }
  return resolveDiyStepLinkedInChannel(row.channel);
}

/** Extra fields for POST /diy-playbooks when step uses LinkedIn subtypes (BE Fix #52b). */
export function diyPlaybookLinkedInSubtypeWire(channel) {
  const ch = getOutboxChannelRaw({ channel });
  if (ch !== 'linkedin_connect' && ch !== 'linkedin_dm') return {};
  const wire = ch === 'linkedin_connect' ? 'connect' : 'dm';
  return {
    channel_subtype: ch,
    channelSubtype: ch,
    linkedin_sub_type: wire,
    linkedinSubType: wire,
  };
}

/** DIY picker value — prefers channelSubtype sidecar when channel is plain `linkedin`. */
export function normalizeLinkedInStepChannelValue(channel, _stepIndex, _allSteps, step) {
  if (step && typeof step === 'object') {
    return resolveDiyStepChannelFromApiStep(step);
  }
  return resolveDiyStepLinkedInChannel(channel);
}

export function sortDiyStepsByOrder(steps) {
  return [...(steps || [])].sort((a, b) => {
    const ao = Number(a?.order ?? 0);
    const bo = Number(b?.order ?? 0);
    return ao - bo;
  });
}

/** True when no earlier touch (by step order) is already LinkedIn. */
export function isFirstLinkedInTouchInSequence(steps, stepId) {
  const sorted = sortDiyStepsByOrder(steps);
  const currentIdx = sorted.findIndex((s) => s?.id === stepId);
  if (currentIdx < 0) return true;
  for (let i = 0; i < currentIdx; i++) {
    if (isLinkedInOutboxChannel({ channel: resolveDiyStepChannelFromApiStep(sorted[i]) })) {
      return false;
    }
  }
  return true;
}

/** First LinkedIn touch in sequence order may offer connection request; later touches are DM only. */
export function canOfferLinkedInConnectionRequest(steps, stepId) {
  return isFirstLinkedInTouchInSequence(steps, stepId);
}

/**
 * First LinkedIn touch: connect or DM (plain `linkedin` → DM).
 * Later LinkedIn touches: always `linkedin_dm`.
 */
export function enforceLinkedInChannelSequence(steps) {
  if (!Array.isArray(steps) || steps.length === 0) return steps;
  const sorted = sortDiyStepsByOrder(steps);
  let seenLinkedIn = false;
  const channelById = new Map();

  for (const step of sorted) {
    const resolved = resolveDiyStepChannelFromApiStep(step);
    if (!isLinkedInOutboxChannel({ channel: resolved })) {
      channelById.set(step.id, step.channel);
      continue;
    }
    let ch = resolved;
    if (seenLinkedIn) ch = 'linkedin_dm';
    else if (ch === 'linkedin_connect' || ch === 'linkedin_dm') seenLinkedIn = true;
    channelById.set(step.id, ch);
  }

  return steps.map((s) => {
    const next = channelById.get(s.id);
    if (next == null || next === s.channel) return s;
    const out = { ...s, channel: next };
    if (next === 'linkedin_dm') {
      delete out.channel_subtype;
      delete out.channelSubtype;
      delete out.linkedin_sub_type;
      delete out.linkedinSubType;
    }
    return out;
  });
}

export function countLinkedInConnectionRequestSteps(steps) {
  if (!Array.isArray(steps)) return 0;
  return steps.filter(
    (s) => resolveDiyStepChannelFromApiStep(s) === 'linkedin_connect',
  ).length;
}

export function channelMatchesEnabledChannels(stepChannel, enabledChannels) {
  if (isLinkedInOutboxChannel({ channel: stepChannel })) {
    return (enabledChannels || []).includes('linkedin');
  }
  return (enabledChannels || []).includes(stepChannel);
}

/**
 * DIY step channel dropdown.
 * First LinkedIn touch in order: DM + connection request; later LinkedIn touches: DM only.
 */
export function buildDiyStepChannelSelectOptions(enabledChannels, { steps, stepId } = {}) {
  const out = [];
  for (const ch of enabledChannels || []) {
    if (ch === 'linkedin') {
      out.push({ value: 'linkedin_dm', label: 'LinkedIn — Direct message' });
      if (!steps?.length || !stepId || canOfferLinkedInConnectionRequest(steps, stepId)) {
        out.push({ value: 'linkedin_connect', label: 'LinkedIn — Connection request' });
      }
    } else if (ch === 'email') {
      out.push({ value: 'email', label: 'Email' });
    } else if (ch === 'call') {
      out.push({ value: 'call', label: 'Call' });
    } else {
      out.push({
        value: ch,
        label: String(ch).charAt(0).toUpperCase() + String(ch).slice(1),
      });
    }
  }
  return out;
}

/** Summarize LinkedIn-ish rows in an outbox list (call once after fetch). */
export function logLinkedInChannelOutboxBatch(context, rows) {
  if (!isLinkedInChannelDebugEnabled()) return;
  const list = Array.isArray(rows) ? rows : [];
  const linkedinRows = list.filter((r) => isLinkedInOutboxChannel(r));
  logLinkedInChannel(`${context}:batch`, {
    total: list.length,
    linkedinCount: linkedinRows.length,
    samples: linkedinRows.slice(0, 8).map((r) => ({
      id: r?.stepId || r?.id,
      stage: getOutboxStageNo(r),
      priorExecutedLinkedIn: r?.__linkedinPriorExecuted ?? null,
      linkedinTouchOrder: getLinkedInTouchOrder(r),
      diyLinkedInChannel: r?.__diyLinkedInChannel ?? null,
      diyPlaybookLinkedInChannel: r?.__diyPlaybookLinkedInChannel ?? null,
      planTemplateChannel: r?.__planTemplateLinkedInChannel ?? null,
      playbookId: getPlaybookIdForOutboxDraft(r) || null,
      stepId: getDraftStepId(r) || null,
      templateChannelRaw: r?.__linkedinTemplateChannelRaw ?? null,
      resolvedChannel: resolveOutboxLinkedInChannel(r),
      fields: collectLinkedInChannelDebugSnapshot(r),
      subtypeLabel: getLinkedInOutboxSubtypeLabel(r),
    })),
  });
}
